import { i18n } from 'src/boot/i18n.js';
import { useFacilitiesStore } from '@stores/facilities.ts';
import { notifyError } from '@boot/popups.js';

/**
 * Class responsible for building benchmark reports from records.
 * @class
 */
export class BenchmarkReportBuilder {
    constructor() {
        // List of facility names
        this.facilities = [];

        // Arrays of metrics for charts
        this.metrics = {
            oee: [],
            performance: [],
            availability: [],
            quality: [],
            running: [],
            planned: [],
            unplanned: []
        };

        // Aggregated metric values
        this.aggregatedMetrics = {
            aggregatedRunningDuration: 0,
            aggregatedPerformanceDowntime: 0,
            aggregatedAvailabilityDowntime: 0,
            aggregatedGrossProduction: 0,
            aggregatedExpectedProduction: 0,
            aggregatedRejectProduction: 0
        };

        // Mapping of record keys to aggregated metric keys
        this.metricsMapping = {
            totalRunningDuration: 'aggregatedRunningDuration',
            totalPerformanceDowntime: 'aggregatedPerformanceDowntime',
            totalAvailabilityDowntime: 'aggregatedAvailabilityDowntime',
            totalGrossProduction: 'aggregatedGrossProduction',
            totalExpectedProduction: 'aggregatedExpectedProduction',
            totalRejectProduction: 'aggregatedRejectProduction'
        };

        // Weights for duration calculations
        this.durationWeights = [];
    }

    /**
     * Builds a benchmark report from the given records.
     * @param {Object[]} records - The records to build the report from.
     * @returns {Object} The benchmark report.
     */
    build(records) {
        this.resetState();

        // Sort records by facility name
        const sortedRecords = [...records].sort((a, b) => {
            const facilityA = this.getFacilityName(a.facility_id);
            const facilityB = this.getFacilityName(b.facility_id);
            return facilityA.localeCompare(facilityB);
        });

        sortedRecords.forEach(record => {
            const formattedRecord = this.formatRecord(record);

            // If the record doesn't have a facility name, skip it
            // This means the user doesn't have access to the facility
            if (!formattedRecord.facilityName) {
                return;
            }

            // Add facility name to facilities array
            this.facilities.push(formattedRecord.facilityName);

            // OEE chart data
            const { oee, performance, availability, quality } = formattedRecord;
            this.metrics.oee.push(this.formatNumber(oee * 100));
            this.metrics.performance.push(this.formatNumber(performance * 100));
            this.metrics.availability.push(
                this.formatNumber(availability * 100)
            );
            this.metrics.quality.push(this.formatNumber(quality * 100));

            // Calculate total period duration
            const totalPeriodDuration =
                formattedRecord.totalRunningDuration +
                formattedRecord.totalPerformanceDowntime +
                formattedRecord.totalAvailabilityDowntime;

            // Add total period duration to weights
            this.durationWeights.push(totalPeriodDuration);

            // Performance chart data
            const { running, planned, unplanned } =
                this.calculateTimeDistributionPercentages(
                    formattedRecord.totalRunningDuration,
                    formattedRecord.totalPlannedDowntime,
                    formattedRecord.totalUnplannedDowntime,
                    totalPeriodDuration
                );

            // Add performance chart data
            this.metrics.running.push(running);
            this.metrics.planned.push(planned);
            this.metrics.unplanned.push(unplanned);

            // Aggregate metrics
            Object.entries(this.metricsMapping).forEach(
                ([recordKey, aggregatedKey]) => {
                    this.aggregatedMetrics[aggregatedKey] +=
                        formattedRecord[recordKey];
                }
            );
        });

        // Add average metrics if there are multiple records
        if (records.length > 1) {
            const averageMetrics = this.calculateAverageMetrics(
                this.metrics,
                this.durationWeights,
                this.aggregatedMetrics
            );

            // Add average metrics to the start of each array
            this.facilities.unshift(i18n.global.t('Average'));
            Object.keys(averageMetrics).forEach(key => {
                if (this.metrics[key]) {
                    this.metrics[key].unshift(averageMetrics[key]);
                }
            });
        }

        return {
            facilities: this.facilities,
            oeeChart: {
                oee: this.metrics.oee,
                performance: this.metrics.performance,
                availability: this.metrics.availability,
                quality: this.metrics.quality
            },
            performanceChart: {
                running: this.metrics.running,
                planned: this.metrics.planned,
                unplanned: this.metrics.unplanned
            }
        };
    }

    /**
     * Resets the internal state of the builder.
     * @private
     */
    resetState() {
        this.facilities = [];
        this.durationWeights = [];
        Object.keys(this.metrics).forEach(key => {
            this.metrics[key] = [];
        });
        Object.keys(this.aggregatedMetrics).forEach(key => {
            this.aggregatedMetrics[key] = 0;
        });
    }

    /**
     * Formats a record from the database into a more readable format.
     * @param {Object} record - The record to format.
     * @returns {Object} The formatted record with camelCase keys and default values
     * @private
     */
    formatRecord(record) {
        return {
            facilityId: record.facility_id,
            facilityName: this.getFacilityName(record.facility_id),
            totalGrossProduction: record.total_gross_production || 0,
            totalRejectProduction: record.total_reject_production || 0,
            totalNetProduction: record.total_net_production || 0,
            totalExpectedProduction: record.total_expected_production || 0,
            totalRunningDuration: record.total_running_duration || 0,
            totalPerformanceDowntime: record.total_performance_downtime || 0,
            totalAvailabilityDowntime: record.total_availability_downtime || 0,
            totalPlannedDowntime: record.total_planned_downtime || 0,
            totalChangeoverDuration: record.total_changeover_duration || 0,
            totalDowntime: record.total_downtime || 0,
            totalUnplannedDowntime: record.total_unplanned_downtime || 0,
            availability:
                record.availability === null ? 0 : record.availability,
            performance: record.performance === null ? 0 : record.performance,
            quality: record.quality === null ? 0 : record.quality,
            oee: record.oee === null ? 0 : record.oee
        };
    }

    /**
     * Gets the facility name from the facility id.
     * @param {number} facilityId - The facility id.
     * @returns {string} The facility name or empty string if not found.
     * @private
     */
    getFacilityName(facilityId) {
        const facilitiesStore = useFacilitiesStore();
        const facilities = facilitiesStore.getFacilities;

        // Handle case where facilities haven't loaded yet
        if (!facilities) {
            return '';
        }

        const facility = facilities.find(
            facility => facility.id === facilityId
        );
        return facility ? facility.name : '';
    }

    /**
     * Calculates the percentage of running, planned, and unplanned downtime.
     * @param {number} running - The total running duration.
     * @param {number} planned - The total planned downtime.
     * @param {number} unplanned - The total unplanned downtime.
     * @param {number} total - The total period duration.
     * @returns {Object} An object containing running, planned, and unplanned percentages.
     * @private
     */
    calculateTimeDistributionPercentages(running, planned, unplanned, total) {
        if (!total) return { running: 0, planned: 0, unplanned: 0 };

        // Calculate raw percentages
        const rawRunning = (running / total) * 100;
        const rawPlanned = (planned / total) * 100;
        const rawUnplanned = (unplanned / total) * 100;

        // If sum is less than or equal to 100, just round and return
        const sum = rawRunning + rawPlanned + rawUnplanned;
        if (sum <= 100) {
            return {
                running: Math.round(rawRunning * 100) / 100,
                planned: Math.round(rawPlanned * 100) / 100,
                unplanned: Math.round(rawUnplanned * 100) / 100
            };
        }

        // If sum exceeds 100, adjust proportionally
        const scale = 100 / sum;
        return {
            running: Math.round(rawRunning * scale * 100) / 100,
            planned: Math.round(rawPlanned * scale * 100) / 100,
            unplanned: Math.round(rawUnplanned * scale * 100) / 100
        };
    }

    /**
     * Calculates the average metrics.
     * @param {Object} metrics - Object containing arrays of metrics.
     * @param {number[]} durationWeights - Array of weights for weighted average calculation.
     * @param {Object} aggregatedMetrics - Object containing aggregated metrics.
     * @returns {Object} The average metrics with the same structure as the input metrics
     * @private
     */
    calculateAverageMetrics(metrics, durationWeights, aggregatedMetrics) {
        const averageMetrics = {
            oee: 0,
            performance: 0,
            availability: 0,
            quality: 0,
            running: 0,
            planned: 0,
            unplanned: 0
        };

        // calculate the OEE metrics averages
        const {
            aggregatedRunningDuration,
            aggregatedPerformanceDowntime,
            aggregatedAvailabilityDowntime,
            aggregatedGrossProduction,
            aggregatedExpectedProduction,
            aggregatedRejectProduction
        } = aggregatedMetrics;

        const averageAvailability =
            (aggregatedRunningDuration + aggregatedPerformanceDowntime) /
            (aggregatedRunningDuration +
                aggregatedPerformanceDowntime +
                aggregatedAvailabilityDowntime);

        const averagePerformance =
            aggregatedGrossProduction / aggregatedExpectedProduction;
        const averageQuality =
            (aggregatedGrossProduction - aggregatedRejectProduction) /
            aggregatedGrossProduction;
        const averageOEE =
            averagePerformance * averageAvailability * averageQuality;

        averageMetrics.oee = this.formatNumber(averageOEE * 100);
        averageMetrics.performance = this.formatNumber(
            averagePerformance * 100
        );
        averageMetrics.availability = this.formatNumber(
            averageAvailability * 100
        );
        averageMetrics.quality = this.formatNumber(averageQuality * 100);

        try {
            // Calculate percentages for performance metrics
            averageMetrics.running = this.calculateWeightedAverage(
                metrics.running,
                durationWeights
            );
            averageMetrics.planned = this.calculateWeightedAverage(
                metrics.planned,
                durationWeights
            );
            averageMetrics.unplanned = this.calculateWeightedAverage(
                metrics.unplanned,
                durationWeights
            );
        } catch (error) {
            averageMetrics.running = 0;
            averageMetrics.planned = 0;
            averageMetrics.unplanned = 0;
            notifyError(
                i18n.global.t(
                    'Failed to calculate average metrics. Please reload the page.'
                ),
                error
            );
        }

        return averageMetrics;
    }

    /**
     * Calculates the weighted average of an array of values.
     * @param {number[]} metricsValues - The array of metrics values.
     * @param {number[]} durationWeights - The array of weights corresponding to the metrics values.
     * @returns {number} The weighted average of the values (0-100).
     * @throws {Error} If metrics values and weights arrays have different lengths.
     * @private
     */
    calculateWeightedAverage(metricsValues, durationWeights) {
        if (metricsValues.length !== durationWeights.length) {
            throw new Error(
                'Metrics values and weights arrays must have the same length'
            );
        }

        const totalWeight = durationWeights.reduce(
            (sum, weight) => sum + weight,
            0
        );

        if (totalWeight === 0) {
            return 0;
        }

        const weightedSum = metricsValues.reduce(
            (sum, value, index) => sum + value * durationWeights[index],
            0
        );

        return Math.round((weightedSum / totalWeight) * 100) / 100;
    }

    /**
     * Formats a number to a fixed decimal places.
     * @param {number} value - The number to format.
     * @returns {number} The formatted number with 2 decimal places.
     * @private
     */
    formatNumber(value) {
        return Math.round(value * 100) / 100;
    }

    /**
     * Builds file rows from the given records.
     * @param {Object[]} records - The records to build the file rows from.
     * @param {string} startDate - The start date of the report period.
     * @param {string} endDate - The end date of the report period.
     * @returns {Array<Array>} Array of rows.
     */
    buildFileRows(records, startDate, endDate) {
        const fileRows = [];
        records.forEach(record => {
            const formattedRecord = this.formatRecord(record);

            fileRows.push([
                formattedRecord.facilityName,
                formattedRecord.totalGrossProduction,
                formattedRecord.totalRejectProduction,
                formattedRecord.totalNetProduction,
                formattedRecord.totalExpectedProduction,
                formattedRecord.totalRunningDuration,
                formattedRecord.totalPerformanceDowntime,
                formattedRecord.totalAvailabilityDowntime,
                formattedRecord.totalPlannedDowntime,
                formattedRecord.totalChangeoverDuration,
                formattedRecord.totalDowntime,
                formattedRecord.totalUnplannedDowntime,
                // get unchanged metrics from record
                record.availability || 0,
                record.performance || 0,
                record.quality || 0,
                record.oee || 0,
                startDate,
                endDate
            ]);
        });
        return fileRows;
    }
}
